/**
 * Tracks in-flight async requests keyed by a string so that repeated calls for
 * the same key while a request is still pending reuse the same promise instead
 * of issuing duplicate requests.
 *
 * This is useful for imperative fetch loops that run inside effects with
 * unstable dependencies: a slow request can be re-triggered many times before
 * it resolves, and without coalescing each re-trigger fires a fresh network
 * call. Once a request settles its slot is cleared, so a later call for the
 * same key starts a fresh request.
 */
export function createInFlightRequests<T>() {
  const pending = new Map<string, Promise<T>>();

  return {
    /** Return the in-flight request for `key`, or start one with `factory`. */
    run(key: string, factory: () => Promise<T>): Promise<T> {
      const existing = pending.get(key);
      if (existing) {
        return existing;
      }

      const request = factory();
      pending.set(key, request);

      const clear = () => {
        // Only clear if this is still the tracked request — a forced refresh
        // may have replaced it with a newer one in the meantime.
        if (pending.get(key) === request) {
          pending.delete(key);
        }
      };
      request.then(clear, clear);

      return request;
    },
    /** Forget any in-flight request for `key` so the next `run` starts fresh. */
    invalidate(key: string) {
      pending.delete(key);
    },
  };
}
